import { css } from 'styled-components';
import { media } from '@minerva-analytics/pantheon';

// Colors in use
export const ORANGE = `#FF5F00`;
export const LIGHT_ORANGE = `#ffece6`;
export const BLUE = `#4C94FF`;
export const LIGHT_BLUE = `#DCEAFF`;
export const GREEN = `#2CE951`;
export const RED = `#FF4646`;
export const YELLOW = `#FFA800`;
export const WHITE = `#fff`;
export const BLACK = `#4A4A4A`;
export const FACEBOOK_BLUE = `#3b5998`;
export const GOOGLE_BLUE = `#4285f4`;
export const TWITTER_BLUE = `#1da1f2`;
export const LIGHT_GRAY = `#d8d8d8`;
export const MEDIUM_GRAY = `#9b9b9b`;

// Button styles
const BUTTON_STYLES = `
  border-radius: 35px;
  font-weight: 800;
  font-size: 1rem;
  border: none;
  transition: .2s ease-in-out;
  padding: 1rem 3rem;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 10px 0 rgba(0,0,0,0.30);
  }
`;

export const PRIMARY_BUTTON = `
  background-color: ${ORANGE};
  color: ${WHITE};
  ${BUTTON_STYLES}
`;

export const SECONDARY_BUTTON = `
  background: none;
  ${BUTTON_STYLES}
  color: ${BLACK};
  border: 3px solid ${BLACK};
`;

export const TERTIARY_BUTTON = `
  color: ${WHITE};
  background-color: ${BLACK};
  ${BUTTON_STYLES}
`;

const setButtonStyle = (type) => {
  switch (type) {
    case `primary`:
      return `
      ${PRIMARY_BUTTON}
    `;
    case `secondary`:
      return `
      ${SECONDARY_BUTTON}
    `;
    case `tertiary`:
      return `
      ${TERTIARY_BUTTON}
    `;
    default:
      return `
      `;
  }
};

// Header styles
const HEADER_STYLES = `
  color: ${BLACK};
  margin: 0;
`;

export const H1 = `
  font-size: 3rem;
  font-weight: 800;
  line-height: 4rem;
  ${HEADER_STYLES}
`;

export const H2 = `
  font-size: 2.2rem;
  font-weight: 800;
  ${HEADER_STYLES}
`;

export const H3 = `
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 2.4rem;
  ${HEADER_STYLES}
  `;

export const H4 = `
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.5rem;
  ${HEADER_STYLES}
`;

export const MENU_ITEM_TEXT = `
  font-size: .85rem;
  font-weight: 600;
`;

// Copy Styles

export const BODY_TEXT = `
  font-size: 1rem;
  line-height: 1.8rem;
  color: ${BLACK};
`;

export const LABEL = `
  font-size: 0.8rem;
  line-height: 1.4rem;
  color: ${BLACK};
`;

export const HEADER_HEIGHT = `5.5rem`;
export const FOOTER_HEIGHT = `4rem`;
export const SUBCATEGORY_SELECTOR_BREAKPOINT = 992;

const setButtonColor = (props) => `
  color: ${props.color ? props.color : MEDIUM_GRAY};
  border: 3px solid ${props.border ? props.border : MEDIUM_GRAY};
  background: ${props.background ? props.background : `transparent`};
`;

const setCardAttributes = (size) => {
  switch (size) {
    case `small`:
      return `
        margin: 0 auto;
        width: 100%;
        max-width: 512px;
      `;
    case `medium`:
      return css`
        width: 100%;
        max-width: 768px;
        margin: 0 auto;
        ${media.tablet`
        `}
      `;
    case `full`:
      return `width: 100%;`;
    default:
      return ``;
  }
};

export const theme = {
  Button: css`
    height: auto;
    padding: 1rem;
    border-radius: 20px;
    line-height: 1.5;

    &, &:hover, :active, :focus {
      outline: none;
      box-shadow: none;
      text-shadow: none;
    }

    ${(props) => setButtonColor(props)}
    ${(props) => setButtonStyle(props.type)}
  `,

  Select: css`
    border: 3px solid ${(props) => props.children.props.color};
    color: ${(props) => props.children.props.color};
    height: auto;
    box-shadow: none;
    border-radius: 1rem;
    line-height: 3;

    &:after {
      border-top: 0.5rem solid ${(props) => props.children.props.color};
    }
  `,

  OptionContainer: css`
    padding: 0;
  `,

  Card: css`
    border-radius: 30px;
    box-shadow: 0 2px 10px 0 rgba(0,0,0,0.10);
    border: none;
    background-color: white;
    padding: 0;
    overflow: hidden;

    ${media.tablet`
      box-shadow: 0 0 0 #000;
    `}

    ${(props) => setCardAttributes(props.size)}
  `,
  TextInput: css`
    font-size: 0.825rem;
    outline: none;
    border: 1px solid ${MEDIUM_GRAY};
    border-radius: 1rem;

    &:focus,
    &:active,
    &:hover {
      border: 1px solid ${ORANGE};
    }
  `,
  Table: css`
    th {
      color: ${WHITE};
      background-color: ${BLUE};
      font-size: 0.825rem;
      padding: 0.66rem;
      border-bottom: 2px solid rgba(0, 0, 0, .06);
      border-top: 0;
      font-weight: normal;
      text-align: left;
      vertical-align: bottom;
    }
    td {
      border-top: 1px solid rgba(0, 0, 0, .06);
      display: table-cell;
      padding: 0.66rem;
      vertical-align: middle;
    }
    tr:nth-child(odd) {
      background-color: ${WHITE};
    }
    tr:nth-child(even) {
      background-color: ${LIGHT_BLUE};
    }
    td, th {
      ${media.phone`
        font-size: 0.75rem;
      `}
    }
    td:first-child,
    th:first-child {
      padding-left: 0.66rem;
    }
    td:last-child,
    th:last-child {
      padding-right: 0.66rem;
    }
  `,
  Link: css`
    color: ${BLUE};
    text-decoration: underline;
  `,
  PageWrap: css`
    padding: 2rem;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 150px;
    height: auto;
    min-height: 100vh;

    ${media.tablet`
      padding: 1rem;
      padding-top: 110px;
    `}
  `,
  DropdownItem: css`
    display: flex;
    justify-content: space-between;
    flex: 0 100%;
    font-family: 'Montserrat',sans-serif;
    -webkit-font-smoothing: antialiased;
    font-size: inherit;
    color: ${BLACK};
    ${MENU_ITEM_TEXT}
    padding: 10px 17px 10px 17px;
    border-bottom: 1px solid ${LIGHT_GRAY};
    width: 100%;
    align-items: center;
    min-height: 46px;
    word-break: break-all;

    &:hover {
      color: ${BLUE};
      cursor: pointer;
    }
    &:last-of-type {
      border-bottom: none;
    }
  `,
};

export default theme;
